import { useState, useEffect, useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { sortFunds } from "@/lib/ledgerLeaderboard";

// Small formatting helpers duplicated locally (same values as LedgerDashboardPage.jsx's
// own fmtMoney/fmtRatio/fmtNavInv — not exported there, and this task's diff contract
// keeps that file's own changes scoped to its cascade branch/imports/editable gates).
function safeCurrencyCode(currency) {
  const n = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(n) ? n : "USD";
}
function fmtMoney(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: safeCurrencyCode(currency), minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}
const fmtRatio = (v) => (v == null ? "—" : Number(v).toFixed(2));
function fmtNavInv(nav, invested) {
  if (nav == null || invested == null || Number(invested) <= 0) return "—";
  return `${(Number(nav) / Number(invested)).toFixed(2)}×`;
}

// Never present a blended cross-currency total: when a rollup's currency disagrees across
// its funds, its scalar nav is null (see bookTree.computeMemberRollup) and this renders one
// amount per currency, largest first, as "X ($) - Y (NIS) - Z (GBP)" — never a blended number.
const CCY_LABEL = { USD: "$", ILS: "NIS", EUR: "€", GBP: "GBP" };
const ccyLabel = (ccy) => {
  const c = String(ccy || "").trim().toUpperCase();
  return CCY_LABEL[c] || c || "?";
};
const fmtPlain = (v) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v);
function fmtNavByCcyChips(navByCcy) {
  const entries = Object.entries(navByCcy || {}).filter(([, v]) => v != null);
  if (entries.length === 0) return "—";
  entries.sort((a, b) => Math.abs(Number(b[1])) - Math.abs(Number(a[1])));
  return entries.map(([ccy, v]) => `${fmtPlain(v)} (${ccyLabel(ccy)})`).join(" - ");
}

const MIXED_CCY_TITLE = "not computed across mixed currencies";

/**
 * MemberCard — middle level of the position-book cascading tree (Portal → Member → Fund).
 * Same shadcn Card/CardContent/Badge idiom as FundRow — collapsed shows the investor name,
 * a rollup chip row (same "label <value>" idiom as FundRow's summary chips), and a
 * fund-count badge; expands to one <FundRow> per fund this investor holds a position in.
 *
 * Props:
 *   memberNode — { investor_name, funds[], rollup } from bookTree.buildBookTree
 *   portalId   — the owning portal id (FundRow needs this for its own badge/keys)
 *   autoExpand — seeds the initial open state (still user-collapsible afterwards)
 *   fundSort   — current "Sort by" metric key — reuses ledgerLeaderboard.sortFunds,
 *                scoped to just this member's own funds[] list
 *   FundRowComponent — the page's FundRow function, passed down as a prop (see PortalCard.jsx
 *                for why this isn't imported directly)
 *   fundRowProps — { editable, compareSet, onCompareToggle, onDiscard, mergeModeActive,
 *                mergeSelection, onMergeToggle, onUnmergeFund, linkMode, linkGroup,
 *                onLinkToggle, onUnlinkMember } — forwarded to every FundRow unchanged,
 *                same props/shape the classic flat render already passes.
 */
export default function MemberCard({ memberNode, portalId, autoExpand = false, fundSort, FundRowComponent, fundRowProps }) {
  const [open, setOpen] = useState(autoExpand);

  useEffect(() => {
    if (autoExpand) setOpen(true);
  }, [autoExpand]);

  const { investor_name, funds, rollup } = memberNode;

  // Scope the page's "Sort by" dropdown to just this member's own funds — reuses
  // ledgerLeaderboard.sortFunds exactly as the classic (flat) render does, just fed a
  // single-member-scoped fund list instead of the whole filtered tree.
  const sortedFunds = useMemo(
    () => sortFunds([{ portal: portalId, funds }], fundSort).map((e) => e.fund),
    [funds, portalId, fundSort]
  );

  const rollupCurrency = rollup.currency && rollup.currency !== "mixed" ? rollup.currency : "USD";
  const mixedCurrency = rollup.currency === "mixed";

  const {
    editable, compareSet, onCompareToggle, onDiscard,
    mergeModeActive, mergeSelection, onMergeToggle, onUnmergeFund,
    linkMode, linkGroup, onLinkToggle, onUnlinkMember,
  } = fundRowProps || {};

  return (
    <Card className="mb-2 max-w-full border-gray-100" data-testid={`member-card-${portalId}-${investor_name}`}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 min-w-0 text-left"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{investor_name}</h3>
              <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                {funds.length} {funds.length === 1 ? "fund" : "funds"}
              </Badge>
              {mixedCurrency && (
                <Badge className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 flex-shrink-0" title="mixed currencies">
                  mixed
                </Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
              {rollup.commitment_total != null && (
                <span>Commitment <span className="font-mono text-gray-900">{fmtMoney(rollup.commitment_total, rollupCurrency)}</span></span>
              )}
              <span>Invested <span className="font-mono text-gray-900">{fmtMoney(rollup.capital_called_cumulative, rollupCurrency)}</span></span>
              <span>Distributed <span className="font-mono text-gray-900">{fmtMoney(rollup.distributions_cumulative, rollupCurrency)}</span></span>
              <span>
                Current Value{" "}
                <span className="font-mono text-gray-900">
                  {mixedCurrency ? fmtNavByCcyChips(rollup.nav_by_ccy) : fmtMoney(rollup.nav, rollupCurrency)}
                </span>
              </span>
              <span>Value/Inv <span className="font-mono text-gray-900">{fmtNavInv(rollup.nav, rollup.capital_called_cumulative)}</span></span>
              <span>
                Net multiple{" "}
                <span className="font-mono text-gray-900" title={mixedCurrency ? MIXED_CCY_TITLE : undefined}>
                  {mixedCurrency ? "—" : fmtRatio(rollup.tvpi)}
                </span>
              </span>
            </div>
          </div>
        </button>

        {open && (
          <div className="border-t border-gray-100 pl-3">
            {sortedFunds.map((fund) => (
              <FundRowComponent
                key={fund.fund_name}
                fund={fund}
                portalId={portalId}
                onDiscard={onDiscard}
                compareSet={compareSet}
                onCompareToggle={onCompareToggle}
                editable={editable}
                mergeModeActive={mergeModeActive}
                mergeSelected={(mergeSelection || []).some(
                  (e) => e.portalId === portalId && e.fund.fund_name === fund.fund_name
                )}
                onMergeToggle={onMergeToggle}
                onUnmergeFund={onUnmergeFund}
                linkMode={linkMode}
                linkGroup={linkGroup}
                onLinkToggle={onLinkToggle}
                onUnlinkMember={onUnlinkMember}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
